/**
 * Locale switch.
 *
 * Two buttons instead of a dropdown: there are only two locales, and a two-option control that is visible is faster
 * to use than a menu that has to be opened. Each button carries `aria-pressed`, so the active language is announced
 * rather than only coloured. The wrapper is a labelled `group` and deliberately carries no `aria-orientation`: that
 * attribute is not defined for `group` (the horizontal arrangement is a visual fact, not an announced one).
 */

import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { useLocale } from '@/hooks/useLocale';
import { SUPPORTED_LOCALES, type AppLocale } from '@/stores/uiStore';

/** Native name of each locale, shown inside the control. */
const LOCALE_LABELS: Record<AppLocale, string> = {
  fa: 'فا',
  en: 'EN',
};

/** Switches the interface language. */
export function LocaleSwitcher({ className }: { className?: string }) {
  const { t } = useTranslation();
  const { locale, changeLocale } = useLocale();

  return (
    <div
      className={className}
      role="group"
      aria-label={t('common.language')}
      // A segmented control: a single bordered group with the buttons sharing its edge.
      data-testid="locale-switcher"
    >
      <div className="flex items-center overflow-hidden rounded-md border border-input">
        {SUPPORTED_LOCALES.map((option) => (
          <Button
            key={option}
            type="button"
            variant={option === locale ? 'secondary' : 'ghost'}
            size="sm"
            aria-pressed={option === locale}
            onClick={() => void changeLocale(option)}
            className="rounded-none"
          >
            {LOCALE_LABELS[option]}
          </Button>
        ))}
      </div>
    </div>
  );
}
